
(() => {
  const INJECTED_STYLE_ATTRIBUTE = 'data-subscription-theme-style';
  const BENEFITS_STYLE_ATTRIBUTE = 'data-subscription-benefits-style';
  const LEARN_MORE_MODAL_STYLE_ATTRIBUTE = 'data-subscription-learn-more-modal-style';
  const LEARN_MORE_MODAL_CONTENT_STYLE_ATTRIBUTE = 'data-subscription-learn-more-modal-content-style';

  const ensureOverlayElement = () => {
    const existing = document.querySelector('.subscription-bottom-black-overlay');
    if (existing) {
      return existing instanceof HTMLElement ? existing : null;
    }

    const overlay = document.createElement('div');
    overlay.className = 'subscription-bottom-black-overlay';
    overlay.setAttribute('data-subscription-bottom-overlay', 'true');
    document.body.appendChild(overlay);

    if (!document.querySelector('style[data-subscription-bottom-overlay-style]')) {
      const overlayStyle = document.createElement('style');
      overlayStyle.setAttribute('data-subscription-bottom-overlay-style', 'true');
      overlayStyle.textContent = `
        .subscription-bottom-black-overlay {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          width: 100% !important;
          height: 100% !important;
          min-height: 100vh !important;
          background-color: rgba(0, 0, 0, 0.6) !important;
          backdrop-filter: blur(5px) !important;
          -webkit-backdrop-filter: blur(5px) !important;
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
          z-index: 99998 !important;
          visibility: hidden !important;
          opacity: 0 !important;
          transition: visibility 0s, opacity 0.3s linear !important;
          pointer-events: none !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        .subscription-bottom-black-overlay.active {
          visibility: visible !important;
          opacity: 1 !important;
        }
      `;
      document.head.appendChild(overlayStyle);
    }

    return overlay;
  };

  /** @type {number} */
  let overlayForceLockUntil = 0;
  /** @type {number | null} */
  let overlayForceLockTimeout = null;

  const forceOverlayLock = (durationMs = 6000) => {
    overlayForceLockUntil = Date.now() + durationMs;
    if (overlayForceLockTimeout) {
      clearTimeout(overlayForceLockTimeout);
    }
    overlayForceLockTimeout = setTimeout(() => {
      overlayForceLockUntil = 0;
      overlayForceLockTimeout = null;
    }, durationMs);
  };

  const releaseOverlayLock = () => {
    overlayForceLockUntil = 0;
    if (overlayForceLockTimeout) {
      clearTimeout(overlayForceLockTimeout);
      overlayForceLockTimeout = null;
    }
  };

  const overlayLockActive = () => overlayForceLockUntil > Date.now();

  /**
   * @param {{ lockDurationMs?: number }} [options]
   */
  const showOverlayGlobal = (options = {}) => {
    const overlay = ensureOverlayElement();
    if (overlay instanceof HTMLElement) {
      overlay.classList.add('active');
      // Force reflow to ensure browser applies styles immediately
      void overlay.offsetHeight;
      if (options.lockDurationMs) {
        forceOverlayLock(options.lockDurationMs);
      }
    }
  };

  const showOverlayWithLock = (durationMs = 6000) => {
    showOverlayGlobal({ lockDurationMs: durationMs });
  };

  const hideOverlayGlobal = (force = false) => {
    if (!force && overlayLockActive()) {
      return;
    }
    if (force) {
      releaseOverlayLock();
    }
    const overlay = document.querySelector('.subscription-bottom-black-overlay');
    if (overlay instanceof HTMLElement) {
      overlay.classList.remove('active');
    }
  };

  /**
   * Finds all modals including those inside shadow roots
   * @returns {HTMLElement[]}
   */
  const findAllModals = () => {
    /** @type {HTMLElement[]} */
    const modals = [];

    // Find modals in light DOM
    document.querySelectorAll('rc-learn-more-modal').forEach((modal) => {
      if (modal instanceof HTMLElement) {
        modals.push(modal);
      }
    });

    /**
     * Recursively walk elements and their shadow roots
     * @param {Element | ShadowRoot} root
     */
    const walkTree = (root) => {
      if (!root) return;

      /** @type {ShadowRoot | null} */
      let shadowRoot = null;

      if (root instanceof Element && root.shadowRoot) {
        shadowRoot = root.shadowRoot;
      } else if (root instanceof ShadowRoot) {
        shadowRoot = root;
      }

      if (shadowRoot) {
        shadowRoot.querySelectorAll('rc-learn-more-modal').forEach((modal) => {
          if (modal instanceof HTMLElement && !modals.includes(modal)) {
            modals.push(modal);
          }
        });
        shadowRoot.querySelectorAll('*').forEach((el) => walkTree(el));
      }
    };

    walkTree(document.documentElement);
    document.querySelectorAll('*').forEach((el) => {
      if (el instanceof Element) {
        walkTree(el);
      }
    });

    return modals;
  };

  /**
   * Checks if modal shadow root content (.rc-modal) is visible
   * @param {HTMLElement} modalNode
   * @returns {boolean}
   */
  const isModalShadowContentVisible = (modalNode) => {
    try {
      const shadowRoot = modalNode.shadowRoot;
      if (!shadowRoot) return false;
      const shadowModal = shadowRoot.querySelector('.rc-modal');
      if (shadowModal instanceof HTMLElement) {
        const style = getComputedStyle(shadowModal);
        const rect = shadowModal.getBoundingClientRect();
        if (
          rect.width > 0 &&
          rect.height > 0 &&
          style.visibility !== 'hidden' &&
          style.display !== 'none' &&
          parseFloat(style.opacity || '1') > 0
        ) {
          return true;
        }
      }
    } catch (error) {
      // Shadow root might be closed or inaccessible
      return false;
    }
    return false;
  };

  /**
   * @param {HTMLElement} modalNode
   */
  const modalIsVisible = (modalNode) => {
    if (!(modalNode instanceof HTMLElement)) return false;
    if (modalNode.hasAttribute('open') || modalNode.hasAttribute('data-open')) return true;
    if (isModalShadowContentVisible(modalNode)) return true;
    const ariaHidden = modalNode.getAttribute('aria-hidden');
    if (ariaHidden === 'true') return false;
    const style = getComputedStyle(modalNode);
    if (style.visibility === 'hidden' || style.display === 'none') return false;
    const rect = modalNode.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };

  /**
   * Continuously monitors modal visibility to keep overlay in sync.
   * @param {HTMLElement} modalNode
   * @param {() => void} showOverlay
   * @param {() => void} hideOverlay
   */
  const startModalOverlayMonitor = (modalNode, showOverlay, hideOverlay) => {
    const trackedModal = /** @type {HTMLElement & { __overlayMonitorActive?: boolean; __overlayMonitorRaf?: number; __overlayLastState?: boolean }} */ (modalNode);
    if (trackedModal.__overlayMonitorActive) return;
    trackedModal.__overlayMonitorActive = true;

    const checkAndUpdate = () => {
      if (!trackedModal.isConnected) {
        trackedModal.__overlayMonitorActive = false;
        return;
      }

      // Fast check: open attribute (most reliable)
      const hasOpen = trackedModal.hasAttribute('open') || trackedModal.hasAttribute('data-open');
      const shadowVisible = isModalShadowContentVisible(trackedModal);
      
      if (hasOpen || shadowVisible) {
        // Modal is open - show overlay IMMEDIATELY
        if (trackedModal.__overlayLastState !== true) {
          console.log('[OverlayDebug] Modal visibility state: visible (open/shadow)', {
            hasOpen: hasOpen,
            shadowVisible,
            ariaHidden: trackedModal.getAttribute('aria-hidden'),
            display: getComputedStyle(trackedModal).display,
            visibility: getComputedStyle(trackedModal).visibility
          });
          trackedModal.__overlayLastState = true;
        }
        showOverlay();
        requestAnimationFrame(showOverlay);
        requestAnimationFrame(() => requestAnimationFrame(showOverlay));
        return;
      }

      // No open attribute - check full visibility
      const visible = modalIsVisible(trackedModal);
      if (trackedModal.__overlayLastState !== visible) {
        console.log('[OverlayDebug] Modal visibility state:', visible ? 'visible' : 'hidden', {
          hasOpen: false,
          ariaHidden: trackedModal.getAttribute('aria-hidden'),
          display: getComputedStyle(trackedModal).display,
          visibility: getComputedStyle(trackedModal).visibility
        });
        trackedModal.__overlayLastState = visible;
      }

      if (visible) {
        showOverlay();
        requestAnimationFrame(showOverlay);
      } else {
        hideOverlay();
      }
    };

    const monitor = () => {
      checkAndUpdate();
      trackedModal.__overlayMonitorRaf = window.requestAnimationFrame(monitor);
    };

    // Check immediately and aggressively on first attach
    checkAndUpdate();
    requestAnimationFrame(checkAndUpdate);
    requestAnimationFrame(() => requestAnimationFrame(checkAndUpdate));
    setTimeout(checkAndUpdate, 0);
    setTimeout(checkAndUpdate, 10);
    setTimeout(checkAndUpdate, 50);
    setTimeout(checkAndUpdate, 100);
    
    // Start continuous monitoring
    monitor();
  };

  /**
   * @param {ShadowRoot | null} root
   */
  const ensureShadowStyles = (root) => {
    if (!root || root.querySelector(`style[${INJECTED_STYLE_ATTRIBUTE}]`)) return;

    const style = document.createElement('style');
    style.setAttribute(INJECTED_STYLE_ATTRIBUTE, 'true');
    style.textContent = `

      fieldset.rc-plans-button-group .rc-plans-button[data-checked],
      rc-selling-plans::part(rc-plans-button__selected) {
        background: #F16A24 !important;
        color: #FFFFFF !important;
        max-width: 64px;
        width: 64px;
        border-radius: 5px;
        padding: 5px 5px;
      }
      fieldset.rc-plans-button-group .rc-plans-button,
      rc-selling-plans::part(rc-plans-button) {
        max-width: 64px;
        width: 64px;
        border-radius: 5px;
        padding: 5px 5px;
      }
      fieldset.rc-plans-button-group,
      rc-selling-plans::part(rc-plans-button-group),
      rc-selling-plans::part(rc-plans-button-list) {
        gap: 8px !important;
        -webkit-tap-highlight-color: transparent !important;
        tap-highlight-color: transparent !important;
      }

      fieldset.rc-plans-button-group:active,
      fieldset.rc-plans-button-group:focus {
        background: transparent !important;
        outline: none !important;
      }
      fieldset.rc-plans-button-group .rc-plans-button[data-checked] .rc-plans-button__interval,
      rc-selling-plans::part(rc-plans-button__selected) .rc-plans-button__interval,
      rc-selling-plans::part(rc-plans-button__interval-selected) {
        color: #FFFFFF !important;
      }

      .rc-purchase-option[rc-selected] {
        border-color: #121212 !important;
      }
      .rc-purchase-option__checked-indicator {
        border-color: #121212 !important;
      }
      .rc-learn-more__trigger,
      .rc-learn-more__trigger:hover {
        color: #121212 !important;
      }

      rc-learn-more button.rc-learn-more__trigger,
      rc-learn-more button.rc-learn-more__trigger:hover {
        color: #121212 !important;
      }

      rc-learn-more::part(rc-learn-more__trigger),
      rc-learn-more::part(rc-learn-more__trigger-compact),
      rc-learn-more::part(rc-learn-more__trigger):hover,
      rc-learn-more::part(rc-learn-more__trigger-compact):hover {
        color: #121212 !important;
      }

      .rc-purchase-option__selector {
      color: var(--Black, #000);  
      font-family: "HvDTrial Brandon Grotesque";
      font-size: 18px!important;
      font-style: normal!important;
      font-weight: 700!important;
      line-height: 28px!important; /* 155.556% */
      }
      .rc-price {
        font-weight: 700!important;
        line-height: 20px!important;
        font-size: 14px!important;
        font-style: normal!important;
      }
      .rc-selection__root { 
        margin-bottom: 16px;
      }
      rc-benefits .rc-benefits__list,
      rc-benefits::part(rc-benefits__list) {
        margin-top: -10px;
      }
      .rc-selection__root {
        color: #000; 
        font-family: "HvDTrial Brandon Grotesque";
        font-size: 14px!important;
        top: 0.15em!important;
        font-style: normal!important;
        font-weight: 400!important;
        line-height: 20px!important;  
      } 
      .rc-plans__label,
      fieldset.rc-plans-button-group > legend,
      rc-selling-plans::part(rc-plans__label) {
        font-size: 12px !important;
        font-weight: 700 !important;
        line-height: normal !important;
        padding: 0 !important;
        padding-bottom: 8px !important;
      }
      .rc-plans-button-group label,
      rc-selling-plans::part(rc-plans-radio-button) {
        display: flex !important;
        flex-grow: 0 !important;
        width: fit-content !important;
        transition: background-color 0.1s ease;
        -webkit-tap-highlight-color: transparent !important;
        tap-highlight-color: transparent !important;
      }

      /* Remove tap highlight and active states from plan buttons */
      .rc-plans-button-group label:active,
      .rc-plans-button-group label:focus,
      .rc-plans-button-group label:focus-visible,
      rc-selling-plans::part(rc-plans-radio-button):active,
      rc-selling-plans::part(rc-plans-radio-button):focus {
        background: transparent !important;
        outline: none !important;
        -webkit-tap-highlight-color: transparent !important;
      }

      .rc-plans-button-group input[type="radio"],
      .rc-plans-button-group input[type="radio"]:active,
      .rc-plans-button-group input[type="radio"]:focus,
      .rc-plans-button-group input[type="radio"]:focus-visible {
        outline: none !important;
        -webkit-tap-highlight-color: transparent !important;
        tap-highlight-color: transparent !important;
      }

      .rc-plans-button-group .rc-plans-button:active,
      .rc-plans-button-group .rc-plans-button:focus,
      .rc-plans-button-group .rc-plans-button:focus-visible,
      rc-selling-plans::part(rc-plans-button):active,
      rc-selling-plans::part(rc-plans-button):focus {
        outline: none !important;
        -webkit-tap-highlight-color: transparent !important;
        box-shadow: none !important;
      }

      /* Remove background shade on click for labels and buttons */
      .rc-plans-button-group label *,
      .rc-plans-button-group label *:active {
        -webkit-tap-highlight-color: transparent !important;
        tap-highlight-color: transparent !important;
      }
 

      .rc-purchase-option__label {
        cursor: pointer !important;
        padding: 16px 32px 16px 32px !important;
      }
      .rc-purchase-option__sub-container {
        cursor: pointer !important;
        padding: 0px 32px 16px 32px !important;
        margin-top: 0px;
      }

        .rc-purchase-option__label {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: flex-start;
          cursor: pointer;
          padding: 0px 32px 8px 33px;
        }
    `;

    root.appendChild(style);
  };

  /**
   * @param {ShadowRoot} root
   */
  const updateSubscriptionLabelText = (root) => {
    if (!root) return;

    root
      .querySelectorAll('[part~="rc-purchase-option__selector_subscription"]')
      .forEach((selectorNode) => {
        if (!(selectorNode instanceof HTMLElement)) return;

        selectorNode.childNodes.forEach((child) => {
          if (child.nodeType !== Node.TEXT_NODE) return;

          const text = child.textContent ?? '';
          if (!text.trim()) return;

          const updated = text.replace('Subscribe', 'Subscription');
          if (updated !== text) {
            child.textContent = updated;
          }
        });
      });
  };

  /**
   * @param {HTMLElement} benefitsNode
   */
  const ensureBenefitsShadowStyles = (benefitsNode) => {
    const benefitsRoot = benefitsNode.shadowRoot;
    if (!benefitsRoot || benefitsRoot.querySelector(`style[${BENEFITS_STYLE_ATTRIBUTE}]`)) return;

    const style = document.createElement('style');
    style.setAttribute(BENEFITS_STYLE_ATTRIBUTE, 'true');
    style.textContent = `
      :host li::before {
        top: 0.15em !important;
        background-color: #121212 !important;
      }
    `;

    benefitsRoot.appendChild(style);
  };

  /**
   * @param {HTMLElement} modalNode
   */
  const ensureLearnMoreModalStyles = (modalNode) => {
    const modalRoot = modalNode.shadowRoot;
    if (!modalRoot || modalRoot.querySelector(`style[${LEARN_MORE_MODAL_STYLE_ATTRIBUTE}]`)) return;

    const style = document.createElement('style');
    style.setAttribute(LEARN_MORE_MODAL_STYLE_ATTRIBUTE, 'true');
    style.textContent = `
      .rc-modal-content {
        height: 50vh !important;
        max-height: 50vh !important;
      }

      @media (max-width: 580px) {
        .rc-modal-content {
          margin: 0 !important;
          max-height: fit-content !important;
          height: auto !important;
          width: 90vw !important;
        }
      }

      /* Remove focus indicators from modal container */
      .rc-modal-content,
      .rc-modal-content:focus,
      .rc-modal-content:focus-visible,
      .rc-modal-content:focus-within {
        outline: none !important;
        border: none !important;
        box-shadow: none !important;
      }

      /* Remove focus from all elements inside modal */
      .rc-modal-content *,
      .rc-modal-content *:focus,
      .rc-modal-content *:focus-visible {
        outline: none !important;
      }

      /* Remove any blue borders or outlines */
      .rc-modal-content::before,
      .rc-modal-content::after {
        display: none !important;
      }

      /* Remove focus from backdrop/overlay and host element */
      :host {
        height: 200vh !important;
        min-height: 200vh !important;
        outline: none !important;
        border: none !important;
      }
      
      :host:focus,
      :host:focus-visible {
        outline: none !important;
        border: none !important;
      }

      :host::before,
      :host::after {
        outline: none !important;
        border: none !important;
      }

      /* Remove focus from any backdrop elements */
      ::backdrop,
      ::backdrop:focus {
        outline: none !important;
        border: none !important;
      }
    `;

    modalRoot.appendChild(style);

    if (!modalNode.querySelector(`style[${LEARN_MORE_MODAL_CONTENT_STYLE_ATTRIBUTE}]`)) {
      const contentStyle = document.createElement('style');
      contentStyle.setAttribute(LEARN_MORE_MODAL_CONTENT_STYLE_ATTRIBUTE, 'true');
      contentStyle.textContent = `
        rc-learn-more-modal .learn-more-footer button {
          min-width: 100px !important;
        }

        @media (max-width: 560px) {
          rc-learn-more-modal h1 {
            font-size: 26px !important;
            line-height: 32px;
            padding-bottom: 16px;
          }
        }

        @media (max-width: 560px) {
          rc-learn-more-modal h2 {
            font-size: 18px !important;
            line-height: 24px;
          }
        }

        /* Remove focus indicators from modal element and container */
        rc-learn-more-modal,
        rc-learn-more-modal:focus,
        rc-learn-more-modal:focus-visible,
        rc-learn-more-modal:focus-within,
        rc-learn-more-modal:active,
        rc-learn-more-modal:hover {
          outline: none !important;
          outline-width: 0 !important;
          outline-style: none !important;
          outline-color: transparent !important;
          border: none !important;
          border-top: none !important;
          border-bottom: none !important;
          border-left: none !important;
          border-right: none !important;
          box-shadow: none !important;
          -webkit-tap-highlight-color: transparent !important;
        }

        /* Remove focus from all children */
        rc-learn-more-modal *,
        rc-learn-more-modal *:focus,
        rc-learn-more-modal *:focus-visible,
        rc-learn-more-modal *:active {
          outline: none !important;
          outline-width: 0 !important;
          -webkit-tap-highlight-color: transparent !important;
        }

        /* Remove pseudo-elements that might show blue lines */
        rc-learn-more-modal::before,
        rc-learn-more-modal::after,
        rc-learn-more-modal::selection {
          display: none !important;
          outline: none !important;
          border: none !important;
        }
      `;

      modalNode.appendChild(contentStyle);
    }
  };

  /**
   * @typedef {ShadowRoot & {
   *   __subscriptionEnhancementsScheduled?: boolean;
   *   __subscriptionStyleObserver?: MutationObserver;
   * }} EnhancedShadowRoot
   */

  /**
   * Prevents body scrolling when modal is open
   * @param {HTMLElement} modalNode
   */
  const preventBodyScroll = (modalNode) => {
    let scrollLocked = false;
    let savedScrollY = 0;

    const isOpen = () => {
      // Check various ways the modal might indicate it's open
      const hasOpenAttr = modalNode.hasAttribute('open');
      const ariaHidden = modalNode.getAttribute('aria-hidden');
      const isAriaVisible = ariaHidden === 'false' || (ariaHidden === null && modalNode.offsetParent !== null);
      const isVisible = modalNode.offsetParent !== null && 
                        modalNode.style.display !== 'none' && 
                        getComputedStyle(modalNode).display !== 'none';
      
      // Check shadow root for modal content visibility
      let shadowContentVisible = false;
      if (modalNode.shadowRoot) {
        const modalContent = modalNode.shadowRoot.querySelector('.rc-modal-content, [class*="modal"]');
        if (modalContent) {
          shadowContentVisible = getComputedStyle(modalContent).display !== 'none';
        }
      }

      return hasOpenAttr || isAriaVisible || (isVisible && shadowContentVisible);
    };

    const lockScroll = () => {
      if (scrollLocked) return;
      
      savedScrollY = window.scrollY;
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${savedScrollY}px`;
      document.body.style.overflow = 'hidden';
      scrollLocked = true;
    };

    const unlockScroll = () => {
      if (!scrollLocked) return;
      
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.overflow = '';
      
      if (savedScrollY !== undefined) {
        window.scrollTo({ top: savedScrollY, behavior: 'instant' });
      }
      
      scrollLocked = false;
      savedScrollY = 0;
    };

    const checkAndUpdateScroll = () => {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        if (isOpen()) {
          lockScroll();
        } else {
          unlockScroll();
        }
      });
    };

    // Check initial state
    checkAndUpdateScroll();

    // Observe for changes on the modal element
    const modalObserver = new MutationObserver(checkAndUpdateScroll);

    modalObserver.observe(modalNode, {
      attributes: true,
      attributeFilter: ['open', 'aria-hidden', 'style', 'class'],
      childList: true,
      subtree: false
    });

    // Also observe the shadow root if it exists
    if (modalNode.shadowRoot) {
      const shadowObserver = new MutationObserver(checkAndUpdateScroll);

      shadowObserver.observe(modalNode.shadowRoot, {
        attributes: true,
        childList: true,
        subtree: true
      });
    }

    // Also listen for click events on close buttons/overlay
    const handleClick = (/** @type {MouseEvent} */ event) => {
      const target = event.target;
      if (target instanceof Element && (
        target.closest('.rc-modal-close, [class*=\'close\'], [class*=\'overlay\']') ||
        target === modalNode
      )) {
        // Delay check to allow modal to close
        setTimeout(checkAndUpdateScroll, 10);
      }
    };

    modalNode.addEventListener('click', handleClick, true);
  };

  /**
   * Removes focus and applies styles to prevent blue line during animation
   * @param {HTMLElement} modalNode
   */
  const removeModalFocus = (modalNode) => {
    // Remove focus immediately
    if (document.activeElement === modalNode) {
      modalNode.blur();
    }
    
    // Apply inline styles directly to remove blue focus line
    modalNode.style.outline = 'none';
    modalNode.style.outlineWidth = '0';
    modalNode.style.outlineStyle = 'none';
    modalNode.style.outlineColor = 'transparent';
    modalNode.style.border = 'none';
    modalNode.style.borderTop = 'none';
    modalNode.style.borderBottom = 'none';
    modalNode.style.borderLeft = 'none';
    modalNode.style.borderRight = 'none';
    modalNode.style.boxShadow = 'none';
    modalNode.style.setProperty('-webkit-tap-highlight-color', 'transparent');
    
    // Also set tabindex to prevent focus
    if (!modalNode.hasAttribute('tabindex')) {
      modalNode.setAttribute('tabindex', '-1');
    }
    
    // Remove focus from any child elements that might be focused
    const focusedElement = modalNode.shadowRoot?.activeElement || modalNode.querySelector(':focus');
    if (focusedElement instanceof HTMLElement) {
      focusedElement.blur();
    }
  };

  /**
   * Adds click listeners inside shadow roots for Learn More triggers
   */
  /**
   * @param {EventTarget} node
   * @returns {boolean}
   */
  const isLearnMoreTriggerNode = (node) => {
    if (!(node instanceof HTMLElement)) return false;
    if (node.classList?.contains('rc-learn-more__trigger')) return true;
    const closestTrigger = node.closest?.('.rc-learn-more__trigger');
    if (closestTrigger) return true;
    if (node.tagName === 'BUTTON') {
      const text = node.textContent?.trim().toLowerCase() || '';
      if (text.includes('learn more')) return true;
    }
    return false;
  };

  /**
   * @param {Document | DocumentFragment | HTMLElement} root
   */
  const setupLearnMoreClickListeners = (root = document) => {
    // Show overlay function
    const showOverlayNow = (lock = false) => {
      if (lock) {
        showOverlayWithLock(4000);
      } else {
        showOverlayGlobal();
      }
    };

    const learnMoreRoot =
      root && typeof root.querySelectorAll === 'function' ? root : document;

    const learnMoreNodes = /** @type {NodeListOf<HTMLElement>} */ (
      learnMoreRoot.querySelectorAll('rc-learn-more')
    );

    learnMoreNodes.forEach((learnMoreNode) => {
      if (learnMoreNode.shadowRoot) {
        const trigger = learnMoreNode.shadowRoot.querySelector('.rc-learn-more__trigger');
        if (trigger instanceof HTMLElement && !trigger.hasAttribute('data-overlay-listener-added')) {
          trigger.setAttribute('data-overlay-listener-added', 'true');
          trigger.addEventListener('click', () => {
            // Show overlay IMMEDIATELY when Learn More is clicked (before modal even opens)
            showOverlayNow(true);
            
            // Aggressive check for modals in shadow roots immediately after click
            const aggressiveCheck = () => {
              const allModals = findAllModals();
              const visibleModal = allModals.find(
                (modal) =>
                  modal.hasAttribute('open') ||
                  modal.hasAttribute('data-open') ||
                  isModalShadowContentVisible(modal) ||
                  modalIsVisible(modal)
              );

              if (visibleModal instanceof HTMLElement) {
                showOverlayWithLock(4000);
                attachModalHandlers(visibleModal);
                return true;
              }
              return false;
            };
            
            // Check immediately and very frequently
            showOverlayNow();
            requestAnimationFrame(() => {
              aggressiveCheck();
              requestAnimationFrame(() => {
                aggressiveCheck();
                requestAnimationFrame(() => aggressiveCheck());
              });
            });
            
            setTimeout(() => { aggressiveCheck(); }, 0);
            setTimeout(() => { aggressiveCheck(); }, 5);
            setTimeout(() => { aggressiveCheck(); }, 10);
            setTimeout(() => { aggressiveCheck(); }, 15);
            setTimeout(() => { aggressiveCheck(); }, 20);
            setTimeout(() => { aggressiveCheck(); }, 25);
            setTimeout(() => { aggressiveCheck(); }, 30);
            setTimeout(() => { aggressiveCheck(); }, 50);
            setTimeout(() => { aggressiveCheck(); }, 75);
            setTimeout(() => { aggressiveCheck(); }, 100);
            setTimeout(() => { aggressiveCheck(); }, 150);
            setTimeout(() => { aggressiveCheck(); }, 200);
            setTimeout(() => { aggressiveCheck(); }, 300);
            
            // Also start the wait function
            waitForModalToBecomeVisible();
          }, true);
        }
      }
    });
  };

  const waitForModalToBecomeVisible = () => {
    const startTime = Date.now();
    const maxDuration = 5000; // 5 seconds

    const attempt = () => {
      applyLearnMoreModalStyles();
      // Check all modals including those in shadow roots
      const allModals = findAllModals();
      const openModal = allModals.find((modal) => 
        modal.hasAttribute('open') ||
        modal.hasAttribute('data-open') ||
        isModalShadowContentVisible(modal) ||
        modalIsVisible(modal)
      );
      
      if (openModal instanceof HTMLElement) {
        showOverlayWithLock(4000);
        requestAnimationFrame(() => showOverlayGlobal());
        requestAnimationFrame(() => requestAnimationFrame(() => showOverlayGlobal()));
        attachModalHandlers(openModal);
        return true;
      }

      return false;
    };

    // Check immediately multiple times
    if (attempt()) return;
    requestAnimationFrame(() => { if (attempt()) return; });
    requestAnimationFrame(() => requestAnimationFrame(() => { if (attempt()) return; }));
    
    setTimeout(() => { if (attempt()) return; }, 0);
    setTimeout(() => { if (attempt()) return; }, 5);
    setTimeout(() => { if (attempt()) return; }, 10);
    setTimeout(() => { if (attempt()) return; }, 15);
    setTimeout(() => { if (attempt()) return; }, 20);
    setTimeout(() => { if (attempt()) return; }, 25);
    setTimeout(() => { if (attempt()) return; }, 50);
    setTimeout(() => { if (attempt()) return; }, 75);
    setTimeout(() => { if (attempt()) return; }, 100);
    setTimeout(() => { if (attempt()) return; }, 150);
    setTimeout(() => { if (attempt()) return; }, 200);
    setTimeout(() => { if (attempt()) return; }, 300);
    setTimeout(() => { if (attempt()) return; }, 400);
    
    // Then check every 25ms for the rest of the duration
    const checkInterval = setInterval(() => {
      if (attempt() || Date.now() - startTime >= maxDuration) {
        clearInterval(checkInterval);
      }
    }, 25);
  };

  /**
   * @param {HTMLElement} modalNode
   */
  const attachModalHandlers = (modalNode) => {
    if (!(modalNode instanceof HTMLElement)) return;

    ensureLearnMoreModalStyles(modalNode);

    // Remove focus immediately to prevent blue line during animation
    removeModalFocus(modalNode);

    const showOverlayNow = () => {
      showOverlayGlobal();
    };

    const hideOverlay = (force = false) => {
      hideOverlayGlobal(force);
    };

    // Add black background overlay at bottom when modal opens to cover iOS navigation area
    if (!modalNode.hasAttribute('data-body-bg-initialized')) {
      modalNode.setAttribute('data-body-bg-initialized', 'true');
      ensureOverlayElement();
    }

    startModalOverlayMonitor(modalNode, showOverlayNow, hideOverlay);

    // IMMEDIATE observer for open attribute - shows overlay instantly when modal opens
    if (!modalNode.hasAttribute('data-open-observer-initialized')) {
      modalNode.setAttribute('data-open-observer-initialized', 'true');
      const openObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === 'attributes' &&
            (mutation.attributeName === 'open' || mutation.attributeName === 'data-open')
          ) {
            const hasOpen = modalNode.hasAttribute('open') || modalNode.hasAttribute('data-open');
            if (hasOpen) {
              // Show overlay IMMEDIATELY when open attribute is added
              showOverlayNow();
              requestAnimationFrame(showOverlayNow);
              requestAnimationFrame(() => requestAnimationFrame(showOverlayNow));
              setTimeout(showOverlayNow, 0);
              setTimeout(showOverlayNow, 10);
              setTimeout(showOverlayNow, 50);
            } else {
              // Hide overlay when open attribute is removed
              hideOverlay(true);
            }
          }
        });
      });
      openObserver.observe(modalNode, {
        attributes: true,
        attributeFilter: ['open', 'data-open']
      });

        if (modalNode.shadowRoot && !modalNode.hasAttribute('data-shadow-visibility-watcher')) {
          modalNode.setAttribute('data-shadow-visibility-watcher', 'true');
          const shadowObserver = new MutationObserver(() => {
            if (isModalShadowContentVisible(modalNode)) {
              showOverlayNow();
              requestAnimationFrame(showOverlayNow);
            }
          });
          shadowObserver.observe(modalNode.shadowRoot, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class']
          });
        }
    }

    // Watch for when modal becomes visible/opens to remove focus during animation
    if (!modalNode.hasAttribute('data-focus-watcher-initialized')) {
      modalNode.setAttribute('data-focus-watcher-initialized', 'true');

      // Observer to catch when modal opens/becomes visible
      const focusObserver = new MutationObserver(() => {
        // Check if modal is opening/visible
        const isOpening =
          modalNode.offsetParent !== null ||
          modalNode.hasAttribute('open') ||
          getComputedStyle(modalNode).display !== 'none';

        if (isOpening) {
          // Use requestAnimationFrame to ensure it happens before/during animation
          requestAnimationFrame(() => {
            removeModalFocus(modalNode);
          });
        }
      });

      focusObserver.observe(modalNode, {
        attributes: true,
        attributeFilter: ['open', 'aria-hidden', 'style', 'class'],
        childList: true,
        subtree: false
      });

      // Also watch shadow root
      if (modalNode.shadowRoot) {
        const shadowObserver = new MutationObserver(() => {
          requestAnimationFrame(() => {
            removeModalFocus(modalNode);
          });
        });

        shadowObserver.observe(modalNode.shadowRoot, {
          attributes: true,
          childList: true,
          subtree: true
        });
      }

      // Listen for any focus events on the modal
      modalNode.addEventListener(
        'focus',
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          removeModalFocus(modalNode);
        },
        true
      );

      modalNode.addEventListener(
        'focusin',
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          removeModalFocus(modalNode);
        },
        true
      );
    }

    // Prevent body scroll when modal is open
    if (!modalNode.hasAttribute('data-scroll-lock-initialized')) {
      modalNode.setAttribute('data-scroll-lock-initialized', 'true');
      preventBodyScroll(modalNode);
    }
  };

  /** Applies shared learn more modal styles across the document. */
  const applyLearnMoreModalStyles = () => {
    // Setup click listeners for Learn More triggers
    setupLearnMoreClickListeners();

    document.querySelectorAll('rc-learn-more-modal').forEach((modalNode) => {
      if (modalNode instanceof HTMLElement) {
        attachModalHandlers(modalNode);
      }
    });
  };

  /**
   * @param {() => void} callback
   */
  const scheduleFrame = (callback) => {
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(callback);
      return;
    }

    window.setTimeout(callback, 16);
  };

  const GRIND_PICKER_SELECTOR = '#grind-picker-inline';
  const SUBSCRIPTION_BLOCK_SELECTOR = '.shopify-block.recharge-subscription-widget';
  /** @type {WeakSet<HTMLElement>} */
  const processedSubscriptionWidgets = new WeakSet();

  /**
   * Finds the Recharge subscription block that belongs to the current buy-buttons block
   * @param {HTMLElement | null} buyButtons
   * @returns {HTMLElement | null}
   */
  const findSubscriptionBlockForBuyButtons = (buyButtons) => {
    if (!(buyButtons instanceof HTMLElement)) return null;

    const hostShopifyBlock = buyButtons.closest('.shopify-block');
    const section = hostShopifyBlock?.closest('.shopify-section');

    /**
     * @param {Element | null} start
     * @param {'previousElementSibling' | 'nextElementSibling'} direction
     * @returns {HTMLElement | null}
     */
    const traverseSiblings = (start, direction) => {
      let current = start;
      while (current instanceof HTMLElement) {
        if (current.matches(SUBSCRIPTION_BLOCK_SELECTOR)) {
          return current;
        }
        current = current[direction];
      }
      return null;
    };

    const siblingMatch =
      traverseSiblings(hostShopifyBlock?.previousElementSibling ?? null, 'previousElementSibling') ||
      traverseSiblings(hostShopifyBlock?.nextElementSibling ?? null, 'nextElementSibling');

    if (siblingMatch) return siblingMatch;

    if (section instanceof HTMLElement) {
      const sectionMatch = section.querySelector(SUBSCRIPTION_BLOCK_SELECTOR);
      if (sectionMatch instanceof HTMLElement) {
        return sectionMatch;
      }
    }

    return document.querySelector(SUBSCRIPTION_BLOCK_SELECTOR);
  };

  /**
   * Moves the Recharge subscription block directly below the grind picker (but above the buttons),
   * keeping the grind radios inside the product form while ensuring they are visually above the widget.
   * Also ensures only the widget for the current product remains inside the form.
   */
  const moveSubscriptionBlockBelowGrindPicker = () => {
    document.querySelectorAll(GRIND_PICKER_SELECTOR).forEach((grindPicker) => {
      if (!(grindPicker instanceof HTMLElement)) return;

      const productForm = grindPicker.closest('form');
      if (!(productForm instanceof HTMLFormElement)) return;

      const productIdInput = productForm.querySelector('input[name="product-id"]');
      const currentProductId =
        productIdInput instanceof HTMLInputElement ? productIdInput.value : null;

      const inFormBlocks = Array.from(
        productForm.querySelectorAll(SUBSCRIPTION_BLOCK_SELECTOR)
      );

      if (currentProductId) {
        inFormBlocks.forEach((block) => {
          if (!(block instanceof HTMLElement)) return;
          const widgetEl = block.querySelector('recharge-subscription-widget');
          const widgetProductId = widgetEl?.getAttribute('product-id');
          if (widgetProductId && widgetProductId !== currentProductId) {
            block.remove();
          }
        });
      }

      const remainingInFormBlocks = Array.from(
        productForm.querySelectorAll(SUBSCRIPTION_BLOCK_SELECTOR)
      );

      if (currentProductId) {
        const hasMatchingBlock = remainingInFormBlocks.some((block) => {
          if (!(block instanceof HTMLElement)) return false;
          const widgetEl = block.querySelector('recharge-subscription-widget');
          const widgetProductId = widgetEl?.getAttribute('product-id');
          return widgetProductId === currentProductId;
        });

        if (hasMatchingBlock) {
          return;
        }
      } else if (remainingInFormBlocks.length > 0) {
        return;
      }

      const buyButtonsElement = productForm.closest('.buy-buttons-block');
      const subscriptionBlock = findSubscriptionBlockForBuyButtons(
        buyButtonsElement instanceof HTMLElement ? buyButtonsElement : null
      );
      if (!(subscriptionBlock instanceof HTMLElement)) return;

      if (currentProductId) {
        const widgetEl = subscriptionBlock.querySelector('recharge-subscription-widget');
        const widgetProductId = widgetEl?.getAttribute('product-id');
        if (widgetProductId && widgetProductId !== currentProductId) {
          return;
        }
      }

      const alreadyPlacedInsideForm =
        subscriptionBlock.parentElement === productForm &&
        subscriptionBlock.getAttribute('data-subscription-placement') === 'inside-buy-buttons';
      if (alreadyPlacedInsideForm) return;

      const buttonsContainer = productForm.querySelector('.product-form-buttons');
      if (buttonsContainer instanceof HTMLElement) {
        productForm.insertBefore(subscriptionBlock, buttonsContainer);
      } else {
        productForm.appendChild(subscriptionBlock);
      }

      subscriptionBlock.setAttribute('data-subscription-placement', 'inside-buy-buttons');
    });
  };

  /**
   * @param {EnhancedShadowRoot} root
   */
  const runEnhancements = (root) => {
    ensureShadowStyles(root);
    updateSubscriptionLabelText(root);
    
    // Setup click listeners for Learn More triggers inside this shadow root
    if (root instanceof Document || root instanceof DocumentFragment) {
      // If it's the document or a fragment, check for rc-learn-more elements
      setupLearnMoreClickListeners(
        /** @type {Document | DocumentFragment | HTMLElement} */ (root)
      );
    }

    const styleRoot = root.querySelector('[data-testid="style-root"]');
    if (styleRoot instanceof HTMLElement) {
      styleRoot.style.setProperty('--rc-widget-brand-color', '#F16A24');
      styleRoot.style.setProperty('--rc-widget-brand-contrast-color', '#FFFFFF');
      styleRoot.style.setProperty('--rc-widget-bullet-icon-image-color', '#121212');
    }

    root.querySelectorAll('rc-benefits').forEach(
      /**
       * @param {Element} benefitsNode
       */
      (benefitsNode) => {
        if (benefitsNode instanceof HTMLElement) {
          benefitsNode.style.setProperty('--rc-widget-bullet-icon-image-color', '#121212');
          ensureBenefitsShadowStyles(benefitsNode);
        }
      }
    );

    root.querySelectorAll('rc-radio-icon.rc-purchase-option__checked-indicator').forEach(
      /**
       * @param {Element} iconNode
       */
      (iconNode) => {
        if (iconNode instanceof HTMLElement) {
          iconNode.style.setProperty('--rc-widget-brand-color', '#121212');
        }
      }
    );

    // Ensure modal handlers inside this shadow root
    const modalsInRoot =
      typeof root.querySelectorAll === 'function'
        ? root.querySelectorAll('rc-learn-more-modal')
        : [];
    modalsInRoot.forEach(
      /**
       * @param {Element} modalNode
       */
      (modalNode) => {
        if (modalNode instanceof HTMLElement) {
          attachModalHandlers(modalNode);
        }
      }
    );
  };

  /**
   * @param {EnhancedShadowRoot} root
   */
  const scheduleEnhancements = (root) => {
    if (root.__subscriptionEnhancementsScheduled) return;

    root.__subscriptionEnhancementsScheduled = true;
    scheduleFrame(() => {
      root.__subscriptionEnhancementsScheduled = false;
      runEnhancements(root);
    });
  };

  /**
   * @param {HTMLElement} widget
   */
  const enhanceWidget = (widget) => {
    const ensureReady = () => {
      const rawRoot = widget.shadowRoot;
      if (!rawRoot) return false;

      const root = /** @type {EnhancedShadowRoot} */ (rawRoot);

      runEnhancements(root);

      if (!root.__subscriptionStyleObserver) {
        const shadowObserver = new MutationObserver(() => {
          scheduleEnhancements(root);
        });

        shadowObserver.observe(root, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['part', 'data-checked', 'class', 'style']
        });

        Object.defineProperty(root, '__subscriptionStyleObserver', {
          value: shadowObserver,
          configurable: false,
          enumerable: false,
          writable: false
        });
      }

      return true;
    };

    if (ensureReady()) return;

    let attempts = 0;
    const maxAttempts = 120; // ~12 seconds
    const interval = window.setInterval(() => {
      attempts += 1;
      if (ensureReady() || attempts >= maxAttempts) {
        window.clearInterval(interval);
      }
    }, 100);
  };

  const applyStyles = () => {
    document.querySelectorAll('recharge-subscription-widget').forEach((widgetNode) => {
      if (widgetNode instanceof HTMLElement) {
        if (processedSubscriptionWidgets.has(widgetNode)) return;
        processedSubscriptionWidgets.add(widgetNode);
        enhanceWidget(widgetNode);
      }
    });

    moveSubscriptionBlockBelowGrindPicker();
    applyLearnMoreModalStyles();
  };

  const observer = new MutationObserver(applyStyles);

  // Watch for new modals being added and immediately check if they're open
  const modalObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement) {
          // Check if the added node itself is a modal
          if (node.matches && node.matches('rc-learn-more-modal')) {
            // Immediately check if it's open and show overlay
            const checkAndShow = () => {
              if (node.hasAttribute('open') || node.hasAttribute('data-open')) {
                showOverlayWithLock(4000);
                requestAnimationFrame(() => showOverlayGlobal());
                requestAnimationFrame(() => requestAnimationFrame(() => showOverlayGlobal()));
              }
            };
            checkAndShow();
            setTimeout(checkAndShow, 0);
            setTimeout(checkAndShow, 10);
            setTimeout(checkAndShow, 50);
            // Apply styles which will also start the monitor
            applyLearnMoreModalStyles();
          }
          // Also check for modals inside the added node
          const modals = node.querySelectorAll?.('rc-learn-more-modal') || [];
          modals.forEach((modal) => {
            if (modal instanceof HTMLElement) {
              const checkAndShow = () => {
                if (modal.hasAttribute('open') || modal.hasAttribute('data-open')) {
                  showOverlayWithLock(4000);
                  requestAnimationFrame(() => showOverlayGlobal());
                }
              };
              checkAndShow();
              setTimeout(checkAndShow, 0);
              setTimeout(checkAndShow, 10);
              applyLearnMoreModalStyles();
            }
          });
        }
      });
    });
  });

  // Continuous check for modals in shadow roots - shows overlay when any modal is open
  const startContinuousModalCheck = () => {
    const checkAllModals = () => {
      const allModals = findAllModals();
      let hasOpenModal = false;
      let hasVisibleModal = false;
      
      allModals.forEach((modal) => {
        if (modal.hasAttribute('open') || modal.hasAttribute('data-open')) {
          hasOpenModal = true;
          hasVisibleModal = true;
          attachModalHandlers(modal);
        } else if (!hasVisibleModal && (isModalShadowContentVisible(modal) || modalIsVisible(modal))) {
          hasVisibleModal = true;
          attachModalHandlers(modal);
        }
      });
      
      if (hasOpenModal || hasVisibleModal) {
        showOverlayWithLock(4000);
        requestAnimationFrame(() => showOverlayGlobal());
      } else {
        hideOverlayGlobal(true);
      }
    };

    // Single immediate check; no continuous timers to avoid performance issues
    checkAllModals();
  };

  const init = () => {
    // Create overlay element early
    ensureOverlayElement();
    
    if (!document.documentElement.hasAttribute('data-learn-more-global-click')) {
      document.documentElement.setAttribute('data-learn-more-global-click', 'true');
      document.addEventListener('click', (event) => {
        const path = event.composedPath ? event.composedPath() : [];
        const matched = path.some((node) => isLearnMoreTriggerNode(node));
        if (matched) {
          // Show overlay immediately
          showOverlayWithLock(4000);
          requestAnimationFrame(() => showOverlayGlobal());
          
          // Aggressive check for modals in shadow roots
          const aggressiveCheck = () => {
            const allModals = findAllModals();
            const visibleModal = allModals.find(
              (modal) =>
                modal.hasAttribute('open') ||
                modal.hasAttribute('data-open') ||
                isModalShadowContentVisible(modal) ||
                modalIsVisible(modal)
            );

            if (visibleModal instanceof HTMLElement) {
              showOverlayWithLock(4000);
              requestAnimationFrame(() => showOverlayGlobal());
              attachModalHandlers(visibleModal);
              return true;
            }
            return false;
          };
          
          // Check very frequently after click
          requestAnimationFrame(() => {
            aggressiveCheck();
            requestAnimationFrame(() => {
              aggressiveCheck();
              requestAnimationFrame(() => aggressiveCheck());
            });
          });
          
          setTimeout(() => { aggressiveCheck(); }, 0);
          setTimeout(() => { aggressiveCheck(); }, 5);
          setTimeout(() => { aggressiveCheck(); }, 10);
          setTimeout(() => { aggressiveCheck(); }, 15);
          setTimeout(() => { aggressiveCheck(); }, 20);
          setTimeout(() => { aggressiveCheck(); }, 25);
          setTimeout(() => { aggressiveCheck(); }, 50);
          setTimeout(() => { aggressiveCheck(); }, 100);
          
          waitForModalToBecomeVisible();
        }
      }, true);
    }

    // Start continuous modal check (including shadow roots)
    startContinuousModalCheck();

    applyStyles();

    /** @type {any} */ (window).SubscriptionWidgetThemeApplyStyles = applyStyles;

    const isDesignMode = document.documentElement.classList.contains('shopify-design-mode');
    if (isDesignMode) {
      document.addEventListener('shopify:section:load', applyStyles);
      document.addEventListener('shopify:block:select', applyStyles);
      observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
    }
    
    // Watch for new modals being added to body
    modalObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  };

  // Setup continuous check for new rc-learn-more elements
  const setupContinuousLearnMoreCheck = () => {
    // Check immediately
    setupLearnMoreClickListeners();
    
    // Check periodically for new elements
    const checkInterval = setInterval(() => {
      setupLearnMoreClickListeners();
    }, 1000); // Check every second
    
    // Also check when DOM changes
    const domObserver = new MutationObserver(() => {
      setupLearnMoreClickListeners();
    });
    
    domObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    // Stop checking after 30 seconds to avoid performance issues
    setTimeout(() => {
      clearInterval(checkInterval);
    }, 30000);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
    document.addEventListener('DOMContentLoaded', setupContinuousLearnMoreCheck, { once: true });
  } else {
    init();
    setupContinuousLearnMoreCheck();
  }
})();